import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalData } from "../hooks/useLocalData";
import {
  ArrowLeft, Trash2, RotateCcw, AlertTriangle, User, FileText, ShieldAlert,
} from "lucide-react";

export default function TrashPage() {
  const navigate = useNavigate();
  const {
    getTrash, restoreIndividual, restoreNote,
    permanentDeleteIndividual, permanentDeleteNote, emptyTrash,
  } = useLocalData();

  const trash = getTrash();
  const [tab, setTab] = useState("individuals");
  const [permDeleteConfirm, setPermDeleteConfirm] = useState(null);
  const [emptyConfirm, setEmptyConfirm] = useState(false);
  const [typedConfirm, setTypedConfirm] = useState("");
  const [restoredId, setRestoredId] = useState(null);

  const handleRestore = (type, id) => {
    if (type === "individual") restoreIndividual(id);
    else restoreNote(id);
    setRestoredId(id);
    setTimeout(() => setRestoredId(null), 2000);
  };

  const handlePermanentDelete = () => {
    if (!permDeleteConfirm || typedConfirm !== "DELETE") return;
    if (permDeleteConfirm.type === "individual") {
      permanentDeleteIndividual(permDeleteConfirm.id);
    } else {
      permanentDeleteNote(permDeleteConfirm.id);
    }
    setPermDeleteConfirm(null);
    setTypedConfirm("");
  };

  const handleEmptyTrash = () => {
    if (typedConfirm !== "EMPTY TRASH") return;
    emptyTrash();
    setEmptyConfirm(false);
    setTypedConfirm("");
  };

  const daysUntilExpiry = (expiresAt) => {
    const diff = new Date(expiresAt) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  return (
    <div className="min-h-screen bg-surface-primary">
      <header
        className="sticky top-0 z-50 px-5 py-4 flex items-center justify-between"
        style={{
          background: "linear-gradient(135deg, #0f1729, #162033)",
          borderBottom: "1px solid rgba(99,179,237,0.1)",
        }}
      >
        <button className="flex items-center gap-3" onClick={() => navigate("/settings")}>
          <ArrowLeft size={20} className="text-gray-300" />
          <span className="text-lg font-bold text-gray-100">Trash</span>
        </button>
        {trash.totalCount > 0 && (
          <button
            className="px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: "rgba(252,129,129,0.08)", color: "#fc8181", border: "1px solid rgba(252,129,129,0.15)" }}
            onClick={() => { setEmptyConfirm(true); setTypedConfirm(""); }}
          >
            Empty Trash
          </button>
        )}
      </header>

      <div className="px-5 py-4">
        {/* Info Banner */}
        <div
          className="glass-card p-4 mb-4 flex items-start gap-3"
          style={{ borderColor: "rgba(236,201,75,0.12)", background: "rgba(236,201,75,0.03)" }}
        >
          <ShieldAlert size={18} className="text-yellow-400 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-yellow-400 mb-0.5">Safety Net Active</div>
            <div className="text-xs text-gray-500 leading-relaxed">
              Deleted items are kept here for 30 days. You can restore them at any time.
              Permanent deletion requires typed confirmation.
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          {[
            { key: "individuals", label: "Individuals", count: trash.individuals.length, icon: User },
            { key: "notes", label: "Notes", count: trash.notes.length, icon: FileText },
          ].map((t) => (
            <button
              key={t.key}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold"
              style={{
                background: tab === t.key ? "rgba(49,130,206,0.2)" : "rgba(15,23,42,0.5)",
                color: tab === t.key ? "#63b3ed" : "#718096",
                border: `1px solid ${tab === t.key ? "rgba(49,130,206,0.3)" : "rgba(99,179,237,0.06)"}`,
              }}
              onClick={() => setTab(t.key)}
            >
              <t.icon size={14} />
              {t.label}
              {t.count > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-yellow-500/20 text-yellow-400">
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Individuals in Trash */}
        {tab === "individuals" && (
          <>
            {trash.individuals.length === 0 ? (
              <div className="text-center mt-12">
                <User size={28} className="text-gray-700 mx-auto mb-3" />
                <div className="text-sm text-gray-500">No individuals in trash</div>
              </div>
            ) : (
              trash.individuals.map((ind) => (
                <div key={ind.id} className="glass-card p-4 mb-3">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="text-base font-bold text-gray-100">
                        {ind.firstName} {ind.lastName}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {ind.program} · DOB: {ind.dob || "—"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] text-yellow-400 font-semibold">
                        {daysUntilExpiry(ind.expiresAt)} days left
                      </div>
                      <div className="text-[10px] text-gray-600">
                        Deleted {new Date(ind.deletedAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold"
                      style={{
                        background: restoredId === ind.id
                          ? "rgba(72,187,120,0.15)"
                          : "rgba(72,187,120,0.08)",
                        color: "#68d391",
                        border: "1px solid rgba(72,187,120,0.15)",
                      }}
                      onClick={() => handleRestore("individual", ind.id)}
                    >
                      <RotateCcw size={13} />
                      {restoredId === ind.id ? "Restored!" : "Restore to Caseload"}
                    </button>
                    <button
                      className="w-11 rounded-xl flex items-center justify-center"
                      style={{ background: "rgba(252,129,129,0.06)", border: "1px solid rgba(252,129,129,0.12)" }}
                      onClick={() => {
                        setPermDeleteConfirm({ type: "individual", id: ind.id, name: `${ind.firstName} ${ind.lastName}` });
                        setTypedConfirm("");
                      }}
                    >
                      <Trash2 size={14} className="text-red-400" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {/* Notes in Trash */}
        {tab === "notes" && (
          <>
            {trash.notes.length === 0 ? (
              <div className="text-center mt-12">
                <FileText size={28} className="text-gray-700 mx-auto mb-3" />
                <div className="text-sm text-gray-500">No notes in trash</div>
              </div>
            ) : (
              trash.notes.map((note) => (
                <div key={note.id} className="glass-card p-4 mb-3">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="text-base font-bold text-gray-100">
                        {note.individualName}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {note.date} · {note.serviceType} · {note.status}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] text-yellow-400 font-semibold">
                        {daysUntilExpiry(note.expiresAt)} days left
                      </div>
                      <div className="text-[10px] text-gray-600">
                        Deleted {new Date(note.deletedAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold"
                      style={{
                        background: restoredId === note.id
                          ? "rgba(72,187,120,0.15)"
                          : "rgba(72,187,120,0.08)",
                        color: "#68d391",
                        border: "1px solid rgba(72,187,120,0.15)",
                      }}
                      onClick={() => handleRestore("note", note.id)}
                    >
                      <RotateCcw size={13} />
                      {restoredId === note.id ? "Restored!" : "Restore Note"}
                    </button>
                    <button
                      className="w-11 rounded-xl flex items-center justify-center"
                      style={{ background: "rgba(252,129,129,0.06)", border: "1px solid rgba(252,129,129,0.12)" }}
                      onClick={() => {
                        setPermDeleteConfirm({ type: "note", id: note.id, name: `${note.individualName} (${note.date})` });
                        setTypedConfirm("");
                      }}
                    >
                      <Trash2 size={14} className="text-red-400" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>

      {/* ===== PERMANENT DELETE CONFIRM ===== */}
      {permDeleteConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 px-8">
          <div
            className="w-full max-w-sm rounded-2xl p-6"
            style={{
              background: "linear-gradient(135deg, #0f1729, #162033)",
              border: "1px solid rgba(252,129,129,0.25)",
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={18} className="text-red-400" />
              <h3 className="text-lg font-bold text-gray-100">Permanently Delete?</h3>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-1">
              Permanently delete <span className="text-gray-200 font-semibold">{permDeleteConfirm.name}</span>?
            </p>
            <p className="text-xs text-red-400 font-semibold mb-4">
              This action cannot be undone. The data will be gone forever.
            </p>

            <div className="mb-4">
              <label className="text-xs text-gray-500 font-semibold block mb-1.5">
                Type <span className="text-red-400 font-bold">DELETE</span> to confirm:
              </label>
              <input
                className="field-input text-sm text-center tracking-widest font-bold"
                style={{
                  borderColor: typedConfirm === "DELETE" ? "rgba(252,129,129,0.4)" : undefined,
                  color: typedConfirm === "DELETE" ? "#fc8181" : undefined,
                }}
                placeholder="DELETE"
                value={typedConfirm}
                onChange={(e) => setTypedConfirm(e.target.value.toUpperCase())}
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <button
                className="flex-1 py-3 rounded-xl text-sm font-semibold"
                style={{ background: "rgba(99,179,237,0.08)", color: "#63b3ed", border: "1px solid rgba(99,179,237,0.2)" }}
                onClick={() => { setPermDeleteConfirm(null); setTypedConfirm(""); }}
              >
                Cancel
              </button>
              <button
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white"
                style={{
                  background: typedConfirm === "DELETE"
                    ? "linear-gradient(135deg, #dc2626, #b91c1c)"
                    : "rgba(252,129,129,0.15)",
                  opacity: typedConfirm === "DELETE" ? 1 : 0.4,
                }}
                onClick={handlePermanentDelete}
                disabled={typedConfirm !== "DELETE"}
              >
                Permanently Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== EMPTY TRASH CONFIRM ===== */}
      {emptyConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 px-8">
          <div
            className="w-full max-w-sm rounded-2xl p-6"
            style={{
              background: "linear-gradient(135deg, #0f1729, #162033)",
              border: "1px solid rgba(252,129,129,0.25)",
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={18} className="text-red-400" />
              <h3 className="text-lg font-bold text-gray-100">Empty All Trash?</h3>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-1">
              Permanently delete <span className="text-gray-200 font-semibold">all {trash.totalCount} items</span> in trash?
            </p>
            <p className="text-xs text-red-400 font-semibold mb-4">
              This action cannot be undone. All data will be gone forever.
            </p>

            <div className="mb-4">
              <label className="text-xs text-gray-500 font-semibold block mb-1.5">
                Type <span className="text-red-400 font-bold">EMPTY TRASH</span> to confirm:
              </label>
              <input
                className="field-input text-sm text-center tracking-widest font-bold"
                style={{
                  borderColor: typedConfirm === "EMPTY TRASH" ? "rgba(252,129,129,0.4)" : undefined,
                  color: typedConfirm === "EMPTY TRASH" ? "#fc8181" : undefined,
                }}
                placeholder="EMPTY TRASH"
                value={typedConfirm}
                onChange={(e) => setTypedConfirm(e.target.value.toUpperCase())}
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <button
                className="flex-1 py-3 rounded-xl text-sm font-semibold"
                style={{ background: "rgba(99,179,237,0.08)", color: "#63b3ed", border: "1px solid rgba(99,179,237,0.2)" }}
                onClick={() => { setEmptyConfirm(false); setTypedConfirm(""); }}
              >
                Cancel
              </button>
              <button
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white"
                style={{
                  background: typedConfirm === "EMPTY TRASH"
                    ? "linear-gradient(135deg, #dc2626, #b91c1c)"
                    : "rgba(252,129,129,0.15)",
                  opacity: typedConfirm === "EMPTY TRASH" ? 1 : 0.4,
                }}
                onClick={handleEmptyTrash}
                disabled={typedConfirm !== "EMPTY TRASH"}
              >
                Empty Trash Forever
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}